import * as React from 'react';
import * as PropTypes from 'prop-types';
import { locationPropType, locationPropTypeShape } from '../components/googlemaps/location';
import { FlightAwareHub } from './flightawarehub';
import * as flightAwareMessages from '../models/flightawaremessages'
import { Location } from '../components/googlemaps/location'
import * as moment from 'moment';
import { getDateStrings } from '../util';

interface FlightAwareTimes {
    flightAwareArrivalUTC: moment.Moment | null;
    flightAwareArrivalLocal: moment.Moment | null;
    flightAwareEstimatedArrivalUTC: moment.Moment | null;
    flightAwareEstimatedArrivalLocal: moment.Moment | null;

    flightAwareArrivalDateUTC: string | null;
    flightAwareArrivalTimeUTC: string | null;
    flightAwareArrivalDateLocal: string | null;
    flightAwareArrivalTimeLocal: string | null;

    flightAwareEstimatedArrivalDateUTC: string | null;
    flightAwareEstimatedArrivalTimeUTC: string | null;
    flightAwareEstimatedArrivalDateLocal: string | null;
    flightAwareEstimatedArrivalTimeLocal: string | null;

    flightAwareDepartureUTC: moment.Moment | null;
    flightAwareDepartureLocal: moment.Moment | null;

    flightAwareDepartureDateUTC: string | null;
    flightAwareDepartureTimeUTC: string | null;
    flightAwareDepartureDateLocal: string | null;
    flightAwareDepartureTimeLocal: string | null;

    enRouteTime?: string | null;
    expired: boolean;
}

export interface FlightAwareFlightLeg extends FlightAwareTimes {
    flight?: Location[],
    plan?: Location[],
    position?: flightAwareMessages.Position,
}

export const flightAwareTrackingPropTypeShape = {
    flight: PropTypes.arrayOf(locationPropType),
    plan: PropTypes.arrayOf(locationPropType),
    position: PropTypes.shape({
        messageType: PropTypes.oneOf(["position"]),
        heading: PropTypes.number,
        ...locationPropTypeShape
    })
}

interface FlightAwareTrackingProps extends FlightAwareTimes{
    flightAwareHub?: FlightAwareHub,
    flightAwareFlightIds: string[],
    arrivalTimeZoneOffset?: number;
    departureTimeZoneOffset?: number;
}

export type TrackedFlightLegProps<P> = FlightAwareTrackingProps & Subtract<P, Subtract<FlightAwareFlightLeg, FlightAwareTimes>>

export function withFlightAwareTracking<P>(WrappedComponent: React.ComponentType<P>) {
    return class WithFlightAwareTracking extends React.Component<TrackedFlightLegProps<P>, FlightAwareFlightLeg> {

        static propTypes = {
            flightAwareHub: PropTypes.instanceOf(FlightAwareHub),
            flightAwareFlightIds: PropTypes.arrayOf(PropTypes.string).isRequired,
            onUpdated: PropTypes.func
        }

        static displayName = `WithFlightAwareTracking(${WrappedComponent.displayName})`;

        state: FlightAwareFlightLeg = {
            flightAwareArrivalUTC: this.props.flightAwareArrivalUTC,
            flightAwareArrivalLocal: this.props.flightAwareArrivalLocal,
            flightAwareArrivalDateUTC: this.props.flightAwareArrivalDateUTC,
            flightAwareArrivalTimeUTC: this.props.flightAwareArrivalTimeUTC,
            flightAwareArrivalDateLocal: this.props.flightAwareArrivalDateLocal,
            flightAwareArrivalTimeLocal: this.props.flightAwareArrivalTimeLocal,
            flightAwareEstimatedArrivalUTC: this.props.flightAwareEstimatedArrivalUTC,
            flightAwareEstimatedArrivalLocal: this.props.flightAwareEstimatedArrivalLocal,
            flightAwareEstimatedArrivalDateUTC: this.props.flightAwareEstimatedArrivalDateUTC,
            flightAwareEstimatedArrivalTimeUTC: this.props.flightAwareEstimatedArrivalTimeUTC,
            flightAwareEstimatedArrivalDateLocal: this.props.flightAwareEstimatedArrivalDateLocal,
            flightAwareEstimatedArrivalTimeLocal: this.props.flightAwareEstimatedArrivalTimeLocal,

            flightAwareDepartureUTC: this.props.flightAwareDepartureUTC,
            flightAwareDepartureLocal: this.props.flightAwareDepartureLocal,
            flightAwareDepartureDateUTC: this.props.flightAwareDepartureDateUTC,
            flightAwareDepartureTimeUTC: this.props.flightAwareDepartureTimeUTC,
            flightAwareDepartureDateLocal: this.props.flightAwareDepartureDateLocal,
            flightAwareDepartureTimeLocal: this.props.flightAwareDepartureTimeLocal,

            expired: this.props.expired
        }

        componentDidMount() {
            this.props.flightAwareHub && this.props.flightAwareHub.listen(this.props.flightAwareFlightIds, this.onMessages);
        }

        componentWillUnmount() {
            this.setStateTimeout && clearTimeout(this.setStateTimeout);
            this.props.flightAwareHub && this.props.flightAwareHub.unlisten(this.props.flightAwareFlightIds, this.onMessages);
        }

        private nextState: {
            arrivalTime?: string;
            departureTime?: string;
            estimatedArrivalTime?: string;
            estimatedDepartureTime?: string;
            enRouteTime?: string;
            expired?: boolean;
            plan?: Location[];
            flight: Location[];
            position?: flightAwareMessages.Position;
        } = {
                flight: []
            };
        private setStateTimeout?: ReturnType<typeof setTimeout>;

        private debouncedSetState = () => {
            const {
                arrivalTime,
                departureTime,
                estimatedArrivalTime,
                estimatedDepartureTime,
                enRouteTime,
                expired,
                plan,
                flight: newFlight,
                position
            } = this.nextState;

            const {
                flightAwareArrivalUTC,
                flightAwareDepartureUTC,
                flightAwareEstimatedArrivalUTC,
                flight
            } = this.state;

            const {
                departureTimeZoneOffset,
                arrivalTimeZoneOffset
            } = this.props;

            if (estimatedArrivalTime) {
                let estimatedArrivalTimeUTC = moment.utc(estimatedArrivalTime);
                if (!flightAwareEstimatedArrivalUTC || !estimatedArrivalTimeUTC.isSame(flightAwareEstimatedArrivalUTC)) {
                    var estimatedArrivalTimeLocal: moment.Moment | null = null;
                    if (arrivalTimeZoneOffset)
                        estimatedArrivalTimeLocal = moment.utc(estimatedArrivalTime).utcOffset(arrivalTimeZoneOffset!)
                    var arrivalTimeStringsUTC = getDateStrings(estimatedArrivalTimeUTC);
                    var arrivalTimeStringsLocal = getDateStrings(estimatedArrivalTimeLocal);
                    this.setState({
                        flightAwareEstimatedArrivalUTC: estimatedArrivalTimeUTC,
                        flightAwareEstimatedArrivalLocal: estimatedArrivalTimeLocal,
                        flightAwareEstimatedArrivalDateUTC: arrivalTimeStringsUTC.date,
                        flightAwareEstimatedArrivalTimeUTC: arrivalTimeStringsUTC.time,
                        flightAwareEstimatedArrivalDateLocal: arrivalTimeStringsLocal.date,
                        flightAwareEstimatedArrivalTimeLocal: arrivalTimeStringsLocal.time
                    })
                }
            }

            if (arrivalTime) {
                let arrivalTimeUTC = moment.utc(arrivalTime);
                if (!flightAwareArrivalUTC || !arrivalTimeUTC.isSame(flightAwareArrivalUTC)) {
                    var arrivalTimeLocal: moment.Moment | null = null;
                    if (arrivalTimeZoneOffset)
                        arrivalTimeLocal = moment.utc(arrivalTime).utcOffset(arrivalTimeZoneOffset!);
                    var arrivalTimeStringsUTC = getDateStrings(arrivalTimeUTC);
                    var arrivalTimeStringsLocal = getDateStrings(arrivalTimeLocal);
                    this.setState({
                        flightAwareArrivalUTC: arrivalTimeUTC,
                        flightAwareArrivalLocal: arrivalTimeLocal,
                        flightAwareArrivalDateUTC: arrivalTimeStringsUTC.date,
                        flightAwareArrivalTimeUTC: arrivalTimeStringsUTC.time,
                        flightAwareArrivalDateLocal: arrivalTimeStringsLocal.date,
                        flightAwareArrivalTimeLocal: arrivalTimeStringsLocal.time
                    })
                }
            }

            if (departureTime) {
                let departureTimeUTC = moment.utc(departureTime);
                if (!flightAwareDepartureUTC || !departureTimeUTC.isSame(flightAwareDepartureUTC)) {
                    var departureTimeLocal: moment.Moment | null = null;
                    if (departureTimeZoneOffset)
                        departureTimeLocal = moment.utc(departureTime).utcOffset(this.props.departureTimeZoneOffset!);
                    var departureTimeStringsUTC = getDateStrings(departureTimeUTC);
                    var departureTimeStringsLocal = getDateStrings(departureTimeLocal);
                    this.setState({
                        flightAwareDepartureUTC: departureTimeUTC,
                        flightAwareDepartureLocal: departureTimeLocal,
                        flightAwareDepartureDateUTC: departureTimeStringsUTC.date,
                        flightAwareDepartureTimeUTC: departureTimeStringsUTC.time,
                        flightAwareDepartureDateLocal: departureTimeStringsLocal.date,
                        flightAwareDepartureTimeLocal: departureTimeStringsLocal.time
                    })
                }
            }

            if (expired) {
                this.setState({ expired: expired });
            }

            if (enRouteTime) {
                this.setState({ enRouteTime });
            }

            if (plan) {
                this.setState({ plan });
            }

            if (position) {
                this.setState({ position });
            }

            if (newFlight.length > 0) {
                //// NOT PUSH. The value of the property needs to change, not the contents.
                this.setState({ flight: (flight || []).concat(newFlight) })
            }

            this.nextState = {
                flight:[]
            };
            this.setStateTimeout = undefined;
        }

        private debounceSetState(state: {
            arrivalTimeUTC?: string;
            departureTimeUTC?: string;
            estimatedArrivalTimeUTC?: string;
            estimatedDepartureTimeUTC?: string;
            enRouteTime?: string;
            expired?: boolean;
            position?: flightAwareMessages.Position;
            plan?: Location[];
            flight?: Location;
        }) {
            this.setStateTimeout && clearTimeout(this.setStateTimeout);
            const {
                flight,
                ...other
            } = state;

            if (flight) {
                this.nextState.flight.push(flight);
            }

            this.nextState = { ...this.nextState, ...other };
            this.setStateTimeout = setTimeout(this.debouncedSetState, 200);
        }

        componentDidUpdate(prevProps: FlightAwareTrackingProps) {
            if (this.props.flightAwareHub !== prevProps.flightAwareHub) {
                prevProps.flightAwareHub && prevProps.flightAwareHub.unlisten(this.props.flightAwareFlightIds, this.onMessages);
                this.props.flightAwareHub && this.props.flightAwareHub.listen(this.props.flightAwareFlightIds, this.onMessages);
            }
        }

        private checkExpired() {
            if (this.state.flightAwareArrivalUTC) {
                var arrivalPlus30 = this.state.flightAwareArrivalUTC.add(30, 'minutes');
                if (moment.utc() > arrivalPlus30) {
                    this.setExpired();
                } else {
                    var duration = moment.duration(arrivalPlus30.diff(moment.utc()));
                    setTimeout(
                        this.setExpired,
                        duration.asMilliseconds());
                }
            }
        }

        private setExpired() {
            this.debounceSetState({ expired: true });
        }

        private onMessages = (messages: flightAwareMessages.FlightMessage[]) => {
            messages.forEach(m => {
                if (m.messageType !== flightAwareMessages.MessageType.Onblock && m.messageType !== flightAwareMessages.MessageType.Offblock) {
                    if (m.estimatedArrivalTime) {
                        this.debounceSetState({ estimatedArrivalTimeUTC: m.estimatedArrivalTime })
                    }

                    if (m.estimatedDepartureTime) {
                        this.debounceSetState({ estimatedDepartureTimeUTC: m.estimatedDepartureTime })
                    }
                }



                if (m.messageType === flightAwareMessages.MessageType.Flightplan && m.enRouteTime) {
                    this.debounceSetState({ enRouteTime : m.enRouteTime })
                }

                if (m.messageType === flightAwareMessages.MessageType.Arrival) {
                    this.debounceSetState({ arrivalTimeUTC: m.arrivalTime });

                    this.checkExpired();
                }

                if (m.messageType === flightAwareMessages.MessageType.Departure) {
                    this.debounceSetState({ departureTimeUTC: m.departureTime });
                }

                switch (m.messageType) {
                    case flightAwareMessages.MessageType.Flightplan:
                        if (m.waypoints) {
                            this.debounceSetState({
                                plan: m.waypoints
                            })
                        }
                        break;
                    case flightAwareMessages.MessageType.Position:
                        this.debounceSetState({
                            position: m
                        });
                        if (m.updateType !== flightAwareMessages.UpdateType.Estimated) {
                            this.debounceSetState({ flight: { latitude: m.latitude, longitude: m.longitude } });
                        }
                        break;
                }
            });
        }

        render() {
            const props = {
                ...this.props,
                ...this.state
            };

            //https://github.com/Microsoft/TypeScript/issues/28938
            // Have to cast props as any since it can't re-merge to the generic P.
            return <WrappedComponent {...props as any}/>
        }
    }
}
